import { ApplicationStatusType } from '@apply/constants';
import { BridgeAppraisalStatus } from '../../../common/constants/BridgeAppraisalStatus';

export interface BridgeApplication {
    applicationId: number;
    applicationStatusTypeId: ApplicationStatusType | undefined;
    appraisalStatusTypeId: BridgeAppraisalStatus | undefined;
    loan: string | undefined;
    borrower: string;
    city: string;
    state: string;
    bfiRefi: boolean;
    units: number;
    estimatedSize: number;
    estimatedRate: number;
    term: string | undefined;
    ltv: number | undefined;
    items: boolean;
    appraised: string | undefined;
    credit: string | undefined;
    experience: string | undefined;
    appliedDate: string | undefined;
    broker: string | undefined;
    note: string | undefined;
    primaryMotivation: string | undefined;
    poc: string;
    appraisalReceivedDate: string | undefined;
    appraisalAmount: number;
    lastUpdated: string; // ISO date
    user: string;
    userEmail: string;
}

export const mockApplicationsGridData: BridgeApplication[] = [
    // =========================================================
    // Incomplete
    // =========================================================
    {
        applicationId: 3,
        applicationStatusTypeId: ApplicationStatusType.Incomplete,
        appraisalStatusTypeId: undefined,
        loan: undefined,
        borrower: 'AJP',
        city: 'Emerald Isle',
        state: 'NC',
        bfiRefi: false,
        units: 1,
        estimatedSize: 875000,
        estimatedRate: 0.091,
        term: undefined,
        ltv: undefined,
        items: false,
        appraised: undefined,
        credit: undefined,
        experience: undefined,
        appliedDate: undefined,
        broker: undefined,
        note: undefined,
        primaryMotivation: undefined,
        poc: 'NE',
        appraisalReceivedDate: undefined,
        appraisalAmount: 0,
        lastUpdated: '02/10/2024',
        user: 'Bob Smith',
        userEmail: 'Bsmith2500DONOTUSE@gmail.com',
    },
    // =========================================================
    // PRE‑APPRAISAL — NothingReceived
    // =========================================================
    {
        applicationId: 13,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.NothingReceived,
        loan: '040B',
        borrower: 'Aspen Ridge Capital',
        city: 'Reno',
        state: 'NV',
        bfiRefi: false,
        units: 1,
        estimatedSize: 875000,
        estimatedRate: 0.091,
        term: '6+6',
        ltv: 0.7,
        items: false,
        appraised: 'N/A',
        credit: 'Fair',
        experience: 'TBD',
        appliedDate: '2024-02-05',
        broker: 'Broker K',
        note: 'Awaiting initial documents',
        primaryMotivation: 'Purchase',
        poc: 'AH',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '01/14/2024',
        user: 'Alice Johnson',
        userEmail: 'Alice8821DONOTUSE@gmail.com',
    },
    {
        applicationId: 14,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.NothingReceived,
        loan: '041B',
        borrower: 'Silver Peak Holdings',
        city: 'Las Vegas',
        state: 'NV',
        bfiRefi: true,
        units: 2,
        estimatedSize: 2100000,
        estimatedRate: 0.089,
        term: '9+6',
        ltv: 0.68,
        items: false,
        appraised: 'N/A',
        credit: 'Strong',
        experience: '5',
        appliedDate: '2024-02-06',
        broker: 'Broker L',
        note: 'Borrower reviewing term sheet',
        primaryMotivation: 'Refinance',
        poc: 'NE',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '03/02/2024',
        user: 'Marcus Lee',
        userEmail: 'Marcus4410DONOTUSE@gmail.com',
    },

    // =========================================================
    // PRE‑APPRAISAL — PaymentReceived
    // =========================================================
    {
        applicationId: 15,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.PaymentReceived,
        loan: '042B',
        borrower: 'Cedar Hill Development',
        city: 'Bozeman',
        state: 'AZ',
        bfiRefi: false,
        units: 1,
        estimatedSize: 1325000,
        estimatedRate: 0.092,
        term: '6+6',
        ltv: 0.7,
        items: false,
        appraised: 'N/A',
        credit: 'Good',
        experience: 'TBD',
        appliedDate: '2024-02-07',
        broker: 'Broker M',
        note: 'Payment received, awaiting survey',
        primaryMotivation: 'Purchase',
        poc: 'KK',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '11/19/2023',
        user: 'Samantha Cruz',
        userEmail: 'Samantha1203DONOTUSE@gmail.com',
    },
    {
        applicationId: 16,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.PaymentReceived,
        loan: '043B',
        borrower: 'Oak Crest Builders',
        city: 'Billings',
        state: 'MT',
        bfiRefi: true,
        units: 1,
        estimatedSize: 950000,
        estimatedRate: 0.093,
        term: '6+6',
        ltv: 0.69,
        items: false,
        appraised: 'N/A',
        credit: 'Strong',
        experience: '10+',
        appliedDate: '2024-02-08',
        broker: 'Broker N',
        note: 'Payment posted, waiting on docs',
        primaryMotivation: 'Refinance',
        poc: 'JL',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '07/28/2024',
        user: 'Daniel Wright',
        userEmail: 'Daniel5577DONOTUSE@gmail.com',
    },

    // =========================================================
    // PRE‑APPRAISAL — SurveyReceived
    // =========================================================
    {
        applicationId: 17,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.SurveyReceived,
        loan: '044B',
        borrower: 'Pine Valley Homes',
        city: 'St. George',
        state: 'UT',
        bfiRefi: false,
        units: 2,
        estimatedSize: 1650000,
        estimatedRate: 0.091,
        term: '9+6',
        ltv: 0.67,
        items: true,
        appraised: 'N/A',
        credit: 'Good',
        experience: 'TBD',
        appliedDate: '2024-02-09',
        broker: 'Broker O',
        note: 'Survey received, awaiting payment',
        primaryMotivation: 'Purchase',
        poc: 'GG',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '09/05/2023',
        user: 'Emily Carter',
        userEmail: 'Emily9034DONOTUSE@gmail.com',
    },
    {
        applicationId: 18,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.SurveyReceived,
        loan: '045B',
        borrower: 'Summit Ridge Partners',
        city: 'Logan',
        state: 'UT',
        bfiRefi: true,
        units: 1,
        estimatedSize: 1180000,
        estimatedRate: 0.089,
        term: '6+6',
        ltv: 0.7,
        items: true,
        appraised: 'N/A',
        credit: 'Excellent',
        experience: '3',
        appliedDate: '2024-02-10',
        broker: 'Broker P',
        note: 'Survey uploaded, waiting on ACH',
        primaryMotivation: 'Refinance',
        poc: 'AH',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '12/22/2024',
        user: 'Jason Patel',
        userEmail: 'Jason3348DONOTUSE@gmail.com',
    },

    // =========================================================
    // PRE‑APPRAISAL — PaymentAndSurveyReceived
    // =========================================================
    {
        applicationId: 19,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.PaymentAndSurveyReceived,
        loan: '046B',
        borrower: 'Redwood Capital',
        city: 'Fresno',
        state: 'CA',
        bfiRefi: false,
        units: 1,
        estimatedSize: 1420000,
        estimatedRate: 0.094,
        term: '6+6',
        ltv: 0.7,
        items: true,
        appraised: 'N/A',
        credit: 'Strong',
        experience: 'TBD',
        appliedDate: '2024-02-11',
        broker: 'Broker Q',
        note: 'Payment + survey received, ready for review',
        primaryMotivation: 'Purchase',
        poc: 'NE',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '04/11/2024',
        user: 'Hannah Brooks',
        userEmail: 'Hannah7712DONOTUSE@gmail.com',
    },
    {
        applicationId: 20,
        applicationStatusTypeId: ApplicationStatusType.SubmittedForReview,
        appraisalStatusTypeId: BridgeAppraisalStatus.PaymentAndSurveyReceived,
        loan: '047B',
        borrower: 'Golden Gate Homes',
        city: 'Sacramento',
        state: 'AZ',
        bfiRefi: true,
        units: 2,
        estimatedSize: 2250000,
        estimatedRate: 0.092,
        term: '9+6',
        ltv: 0.68,
        items: true,
        appraised: 'N/A',
        credit: 'Good',
        experience: '8',
        appliedDate: '2024-02-12',
        broker: 'Broker R',
        note: 'All items received, pending internal check',
        primaryMotivation: 'Refinance',
        poc: 'KK',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '08/03/2023',
        user: 'Robert Miles',
        userEmail: 'Robert2689DONOTUSE@gmail.com',
    },

    // =========================================================
    // BFI TO ORDER (Key Items Satisfactory)
    // =========================================================
    {
        applicationId: 1,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.ReadyForAppraisal,
        loan: '011B',
        borrower: 'V5 Holdings',
        city: 'San Antonio',
        state: 'TX',
        bfiRefi: true,
        units: 1,
        estimatedSize: 2000000,
        estimatedRate: 0.09,
        term: '9+6',
        ltv: 0.7,
        items: false,
        appraised: 'Ready',
        credit: 'Good',
        experience: '20+',
        appliedDate: '2024-01-12',
        broker: 'Broker A',
        note: 'Alright to pay later',
        primaryMotivation: 'Refinance',
        poc: 'AH',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '10/17/2024',
        user: 'Natalie Kim',
        userEmail: 'Natalie5520DONOTUSE@gmail.com',
    },
    {
        applicationId: 2,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.ReadyForAppraisal,
        loan: '015B',
        borrower: 'Green Homes',
        city: 'Sandpoint',
        state: 'ID',
        bfiRefi: false,
        units: 1,
        estimatedSize: 1395000,
        estimatedRate: 0.095,
        term: '9+6',
        ltv: 0.68,
        items: true,
        appraised: 'Ready',
        credit: 'Strong',
        experience: '10+',
        appliedDate: '2024-01-15',
        broker: 'Broker B',
        note: 'Expects CO in 2 weeks',
        primaryMotivation: 'Purchase',
        poc: 'NE',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '06/29/2024',
        user: 'Trevor Hall',
        userEmail: 'Trevor9911DONOTUSE@gmail.com',
    },
    {
        applicationId: 3,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.ReadyForAppraisal,
        loan: '018B',
        borrower: 'Gold View Homes',
        city: 'American Fork',
        state: 'UT',
        bfiRefi: true,
        units: 1,
        estimatedSize: 1200000,
        estimatedRate: 0.0925,
        term: '6+6',
        ltv: 0.72,
        items: true,
        appraised: 'Ready',
        credit: 'Good',
        experience: 'TBD',
        appliedDate: '2024-01-18',
        broker: 'Broker C',
        note: 'Waiting on appraisal scheduling',
        primaryMotivation: 'Refinance',
        poc: 'KK',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '02/07/2023',
        user: 'Laura Bennett',
        userEmail: 'Laura4482DONOTUSE@gmail.com',
    },

    // =========================================================
    // ORDERED
    // =========================================================
    {
        applicationId: 4,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.AppraisalOrdered,
        loan: '020B',
        borrower: 'Canyon Estates',
        city: 'Boise',
        state: 'ID',
        bfiRefi: false,
        units: 2,
        estimatedSize: 1000000,
        estimatedRate: 0.094,
        term: '6+6',
        ltv: 0.7,
        items: true,
        appraised: 'Ordered',
        credit: 'Fair',
        experience: 'TBD',
        appliedDate: '2024-01-20',
        broker: 'Broker D',
        note: 'Appraiser assigned',
        primaryMotivation: 'Purchase',
        poc: 'KK',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '05/15/2024',
        user: 'Kevin Adams',
        userEmail: 'Kevin7745DONOTUSE@gmail.com',
    },
    {
        applicationId: 5,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.AppraisalOrdered,
        loan: '021B',
        borrower: 'Highland Properties',
        city: 'Spokane',
        state: 'WA',
        bfiRefi: true,
        units: 1,
        estimatedSize: 3950000,
        estimatedRate: 0.091,
        term: '9+6',
        ltv: 0.69,
        items: true,
        appraised: 'Ordered',
        credit: 'Good',
        experience: 'TBD',
        appliedDate: '2024-01-22',
        broker: 'Broker E',
        note: 'Inspection scheduled',
        primaryMotivation: 'Refinance',
        poc: 'KK',
        appraisalReceivedDate: '08/25/2026',
        appraisalAmount: 0,
        lastUpdated: '09/30/2024',
        user: 'Michelle Grant',
        userEmail: 'Michelle2208DONOTUSE@gmail.com',
    },
    {
        applicationId: 6,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.AppraisalOrdered,
        loan: '022B-ORD',
        borrower: 'Mountain View Capital',
        city: 'Twin Falls',
        state: 'ID',
        bfiRefi: false,
        units: 1,
        estimatedSize: 1669500,
        estimatedRate: 0.093,
        term: '6+6',
        ltv: 0.7,
        items: true,
        appraised: 'Ordered',
        credit: 'Strong',
        experience: '10+',
        appliedDate: '2024-01-23',
        broker: 'Broker F',
        note: 'Awaiting property access',
        primaryMotivation: 'Purchase',
        poc: 'KK',
        appraisalReceivedDate: '',
        appraisalAmount: 0,
        lastUpdated: '03/26/2023',
        user: 'Patrick Doyle',
        userEmail: 'Patrick6633DONOTUSE@gmail.com',
    },

    // =========================================================
    // APPRAISED (received & review complete)
    // =========================================================
    {
        applicationId: 7,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.Appraised,
        loan: '022B',
        borrower: 'Blue Sky Properties',
        city: 'Denver',
        state: 'CO',
        bfiRefi: false,
        units: 2,
        estimatedSize: 950000,
        estimatedRate: 0.089,
        term: '9+6',
        ltv: 0.65,
        items: true,
        appraised: '$1,395,000',
        credit: 'Strong',
        experience: '8',
        appliedDate: '2024-01-25',
        broker: 'Broker F',
        note: 'Pending review',
        primaryMotivation: 'Purchase',
        poc: 'JL',
        appraisalReceivedDate: '07/25/2026',
        appraisalAmount: 211201,
        lastUpdated: '07/09/2024',
        user: 'Vanessa Ortiz',
        userEmail: 'Vanessa5017DONOTUSE@gmail.com',
    },
    {
        applicationId: 8,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.Appraised,
        loan: '023B',
        borrower: 'Sunrise Development',
        city: 'Mesa',
        state: 'AZ',
        bfiRefi: false,
        units: 3,
        estimatedSize: 4000000,
        estimatedRate: 0.09,
        term: '9+6',
        ltv: 0.67,
        items: true,
        appraised: '$2,000,000',
        credit: 'Excellent',
        experience: '5',
        appliedDate: '2024-01-28',
        broker: 'Broker G',
        note: 'Review complete',
        primaryMotivation: 'Refinance',
        poc: 'KK',
        appraisalReceivedDate: '07/25/2026',
        appraisalAmount: 659000,
        lastUpdated: '11/04/2023',
        user: 'George Simmons',
        userEmail: 'George8890DONOTUSE@gmail.com',
    },
    {
        applicationId: 9,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.Appraised,
        loan: '024B',
        borrower: 'Heritage Builders',
        city: 'Colorado Springs',
        state: 'CO',
        bfiRefi: false,
        units: 2,
        estimatedSize: 2378000,
        estimatedRate: 0.087,
        term: '9+6',
        ltv: 0.64,
        items: true,
        appraised: '$1,250,000',
        credit: 'Strong',
        experience: '10+',
        appliedDate: '2024-01-29',
        broker: 'Broker H',
        note: 'Valuation confirmed',
        primaryMotivation: 'Purchase',
        poc: 'KK',
        appraisalReceivedDate: '07/25/2026',
        appraisalAmount: 150000,
        lastUpdated: '01/31/2024',
        user: 'Rachel Moore',
        userEmail: 'Rachel3156DONOTUSE@gmail.com',
    },

    // =========================================================
    // APPROVED / WAITING TO CLOSE
    // =========================================================
    {
        applicationId: 10,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.ApprovedAndWaitingToClose,
        loan: '030B',
        borrower: 'Prime Builders',
        city: 'Phoenix',
        state: 'AZ',
        bfiRefi: false,
        units: 3,
        estimatedSize: 2062000,
        estimatedRate: 0.091,
        term: '9+6',
        ltv: 0.7,
        items: true,
        appraised: '$2,000,000',
        credit: 'Excellent',
        experience: '2',
        appliedDate: '2024-02-01',
        broker: 'Broker H',
        note: 'Cleared to close',
        primaryMotivation: 'Refinance',
        poc: 'KK',
        appraisalReceivedDate: '07/25/2026',
        appraisalAmount: 225000,
        lastUpdated: '10/12/2024',
        user: 'Anthony Rivera',
        userEmail: 'Anthony7429DONOTUSE@gmail.com',
    },
    {
        applicationId: 11,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.ApprovedAndWaitingToClose,
        loan: '031B',
        borrower: 'Evergreen Homes',
        city: 'Salt Lake City',
        state: 'UT',
        bfiRefi: false,
        units: 2,
        estimatedSize: 1368102,
        estimatedRate: 0.088,
        term: '9+6',
        ltv: 0.66,
        items: true,
        appraised: '$1,750,000',
        credit: 'Strong',
        experience: 'TBD',
        appliedDate: '2024-02-03',
        broker: 'Broker I',
        note: 'Docs out for signing',
        primaryMotivation: 'Purchase',
        poc: 'GG',
        appraisalReceivedDate: '07/25/2026',
        appraisalAmount: 30000,
        lastUpdated: '04/08/2024',
        user: 'Jennifer Collins',
        userEmail: 'Jennifer6734DONOTUSE@gmail.com',
    },
    {
        applicationId: 12,
        applicationStatusTypeId: ApplicationStatusType.Appraise,
        appraisalStatusTypeId: BridgeAppraisalStatus.ApprovedAndWaitingToClose,
        loan: '032B',
        borrower: 'Liberty Construction',
        city: 'Provo',
        state: 'UT',
        bfiRefi: true,
        units: 1,
        estimatedSize: 1169702,
        estimatedRate: 0.092,
        term: '9+6',
        ltv: 0.7,
        items: true,
        appraised: '$1,300,000',
        credit: 'Good',
        experience: 'TBD',
        appliedDate: '2024-02-04',
        broker: 'Broker J',
        note: 'Scheduling closing',
        primaryMotivation: 'Refinance',
        poc: 'JL',
        appraisalReceivedDate: '07/25/2026',
        appraisalAmount: 275000,
        lastUpdated: '12/03/2023',
        user: 'Brian Thompson',
        userEmail: 'Brian9421DONOTUSE@gmail.com',
    },
];
